import type { TerminologyTermValue } from '~/_api/types/interfaces/terminology'

/**
 * Module-level singleton holding the active account's resolved terminology
 * dictionary (sourced entirely from `auth.info.terminology` — no client-side
 * copy of the default catalog words). Kept in sync by
 * `app/plugins/terminology.client.ts` whenever `authStore.info.terminology`
 * changes. A plain ref (not a Pinia store) so it can be read from the
 * vue-i18n `postTranslation` hook, which runs outside normal
 * component/store context — see `app/plugins/i18n.ts`.
 */
const dictionary = ref<Record<string, TerminologyTermValue>>({})

export function setTerminologyDictionary(dict: Record<string, TerminologyTermValue> | undefined | null) {
  dictionary.value = dict ?? {}
}

// Generic English pluralization (not a per-term word catalog) for the
// fallback below — covers every current catalog key correctly (course →
// courses, class → classes, giftCard → giftCards, ...).
function naivePlural(word: string): string {
  return /[sxz]$|[cs]h$/i.test(word) ? `${word}es` : `${word}s`
}

/**
 * Falls back to the bare key (e.g. "employee"/"employees") if the
 * dictionary hasn't loaded yet — there's no bundled default-word catalog
 * to fall back to instead, since the API is the single source of truth
 * for term wording.
 */
export function resolveTerm(key: string, plural: boolean): string {
  const entry = dictionary.value[key]
  if (!entry) return plural ? naivePlural(key) : key
  return plural ? entry.plural : entry.singular
}

// Matches the same `:term(x)` / `:terms(x)` / `:Term(x)` / `:Terms(x)` /
// `:TERM(x)` / `:TERMS(x)` placeholder convention used server-side by
// `App\Services\Terminology\TerminologyTranslator` (manager-www), so message
// authors use one consistent syntax across PHP and Vue i18n strings.
const TOKEN_PATTERN = /:(Terms|TERMS|Term|TERM|terms|term)\(([a-zA-Z]+)\)/g

// Stored words are Title Case, so `:term()`/`:terms()` lowercase each word
// for mid-sentence use and `:Term()`/`:Terms()` sentence-case them. Words
// with deliberate inner casing (eGift, SMS, iPad) are left as entered.
const isPlainWord = (part: string) => part.slice(1) === part.slice(1).toLowerCase()

function applyCase(word: string, tokenCase: string): string {
  if (tokenCase === 'TERM' || tokenCase === 'TERMS') return word.toUpperCase()
  const sentenceCase = tokenCase === 'Term' || tokenCase === 'Terms'
  let first = true
  return word.replace(/[^\s-]+/g, (part) => {
    const isFirst = first
    first = false
    if (!isPlainWord(part)) return part
    const head = isFirst && sentenceCase ? part.charAt(0).toUpperCase() : part.charAt(0).toLowerCase()
    return head + part.slice(1)
  })
}

/**
 * Replaces terminology placeholders inside a translated string with the
 * active account's effective words. Registered as vue-i18n's
 * `postTranslation` handler (see `app/plugins/i18n.ts`), so any `$t()` /
 * `t()` call resolves tokens automatically — no per-call-site code needed,
 * just embed `:term(key)` / `:terms(key)` in the message text.
 */
export function applyTerminologyTokens(text: string): string {
  if (!text || !text.includes(':')) return text
  return text.replace(TOKEN_PATTERN, (_match, tokenCase: string, key: string) => {
    const plural = tokenCase.toLowerCase() === 'terms'
    return applyCase(resolveTerm(key, plural), tokenCase)
  })
}

export function useTerminology() {
  const term = (key: string, plural = false) => resolveTerm(key, plural)
  return {
    term,
    dictionary: computed(() => dictionary.value),
  }
}
